import { memo, useCallback, useState } from 'react';
import { DialogTrigger } from 'react-aria-components';

import SaveIcon from '@/assets/save-icon.svg?react';
import { Button, Flex, Heading, Separator, Text } from '@/components/core';
import { ExportOption, NewExportModal, NewTriggerModal, TriggerOption } from '@/components/flow';
import { ChevronSelectorIcon, InfoFilledIcon } from '@/components/icons';
import { ExportLogo, TriggerLogo } from '@/components/logos';
import { Step, StepProps } from '@/features/agents';

export type ChooseIntegrationResult = {
  trigger?: TriggerOption;
  export?: ExportOption;
};

export type ChooseIntegrationsStepProps = StepProps & {
  onContinue: (data: ChooseIntegrationResult) => void;
  onBack?: (data: ChooseIntegrationResult) => void;
  isCreatingAgent?: boolean;
  defaultOptions?: ChooseIntegrationResult;
};

const IChooseIntegrationsStep = ({
  onContinue,
  defaultOptions,
  isCreatingAgent,
  ...rest
}: ChooseIntegrationsStepProps) => {
  const [selectedTrigger, setSelectedTrigger] = useState<TriggerOption | undefined>(defaultOptions?.trigger);
  const [triggerModalOpened, setTriggerModalOpened] = useState(false);
  const onTriggerOptionSelected = useCallback((triggerOption: TriggerOption) => {
    setSelectedTrigger(triggerOption);
    setTriggerModalOpened(false);
  }, []);

  const [selectedExport, setSelectedExport] = useState<ExportOption | undefined>(defaultOptions?.export);
  const [exportModalOpened, setExportModalOpened] = useState(false);
  const onExportOptionSelected = useCallback((exportOption: ExportOption) => {
    setSelectedExport(exportOption);
    setExportModalOpened(false);
  }, []);

  return (
    <Step {...rest}>
      <Flex col className="gap-lg">
        <Flex col className="px-md gap-xs">
          <Heading size="md">How do you plan to integrate?</Heading>
          <Text>Where are your documents coming from and where are they going?</Text>
        </Flex>

        <Flex col className="px-md gap-md">
          <DialogTrigger>
            <Button variant="plain" className="border-1 border-gray-200" onPress={() => setTriggerModalOpened(true)}>
              <TriggerLogo size="lg" functionId={selectedTrigger?.functionId} />
              <Text className="flex-1 text-start">{selectedTrigger?.name ?? 'Select trigger'}</Text>
              <ChevronSelectorIcon />
            </Button>

            <NewTriggerModal
              isOpen={triggerModalOpened}
              onOpenChange={() => setTriggerModalOpened(false)}
              onTriggerOptionSelected={onTriggerOptionSelected}
            />
          </DialogTrigger>

          <DialogTrigger>
            <Button variant="plain" className="border-1 border-gray-200" onPress={() => setExportModalOpened(true)}>
              <ExportLogo size="lg" functionId={selectedExport?.functionId} />
              <Text className="flex-1 text-start">{selectedExport?.name ?? 'Select export'}</Text>
              <ChevronSelectorIcon />
            </Button>

            <NewExportModal
              isOpen={exportModalOpened}
              onExportOptionSelected={onExportOptionSelected}
              onOpenChange={() => setExportModalOpened(false)}
            />
          </DialogTrigger>

          <Flex className="p-sm gap-sm rounded-lg bg-gray-50" variant="default">
            <InfoFilledIcon />
            <Text size="xs">We'll use this to setup your agent. You can change this later.</Text>
          </Flex>
        </Flex>

        <Separator orientation="horizontal" />

        <Flex col className="px-md">
          <Button
            variant="primary"
            isDisabled={isCreatingAgent || !selectedTrigger || !selectedExport}
            onPress={() => onContinue({ trigger: selectedTrigger, export: selectedExport })}
          >
            {isCreatingAgent ? <SaveIcon className="size-md animate-spin stroke-gray-500" /> : null}
            <Text>Create agent</Text>
          </Button>
        </Flex>
      </Flex>
    </Step>
  );
};

export const ChooseIntegrationsStep = memo(IChooseIntegrationsStep) as typeof IChooseIntegrationsStep;
